import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMap, faTachometerAlt, faMicrochip, faBell,
  faCog, faUsers, faSignOutAlt, faBars, faTimes, faTasks
} from "@fortawesome/free-solid-svg-icons";
import Logo from "./Logo.jsx";
import ParticleBackground from "./ParticleBackground.jsx";
import { useAuthStore } from "../../lib/store.js";
import api from "../../lib/api.js";

const USER_NAV = [
  { to: "/",         icon: faMap,           label: "Map" },
  { to: "/devices",  icon: faMicrochip,     label: "Devices" },
  { to: "/alerts",   icon: faBell,          label: "Alerts" },
  { to: "/settings", icon: faCog,           label: "Settings" },
];

const ADMIN_NAV = [
  { to: "/dashboard",     icon: faTachometerAlt, label: "Dashboard" },
  { to: "/admin/devices", icon: faMicrochip,     label: "Devices" },
  { to: "/admin/users",   icon: faUsers,         label: "Users" },
  { to: "/jobs",          icon: faTasks,         label: "Jobs", external: true },
];

export default function Layout({ children }) {
  const user      = useAuthStore((s) => s.user);
  const clearUser = useAuthStore((s) => s.clearUser);
  const navigate  = useNavigate();
  const location  = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  async function handleSignOut() {
    // Sign-out is a Devise route, not under /api/v1
    await fetch("/users/sign_out", {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    }).catch(() => {});
    clearUser();
    navigate("/login");
  }

  return (
    <div
      className="bg-grid scanlines"
      style={{ background: "var(--color-void)", height: "100%", display: "flex", flexDirection: "column" }}
    >
      <ParticleBackground />

      {/* Mobile header bar */}
      <header
        className="mobile-header"
        style={{
          position: "relative", zIndex: 20,
          alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px",
          background: "var(--color-abyss)",
          borderBottom: "1px solid var(--color-border)",
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setSidebarOpen(true)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-muted)", fontSize: 18, padding: 4 }}
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
        <Logo size={38} fontSize={22} showText />
        <div style={{ width: 26 }} /> {/* spacer */}
      </header>

      {/* Main area */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
        {/* Overlay (mobile) */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay"
            style={{ display: "block" }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`sidebar relative z-10 flex flex-col border-r ${sidebarOpen ? "open" : ""}`}
          style={{
            width: 220, background: "var(--color-abyss)",
            borderColor: "var(--color-border)", flexShrink: 0,
          }}
        >
          {/* Logo — desktop */}
          <div
            className="mobile-hide p-5 border-b flex items-center justify-between"
            style={{ borderColor: "var(--color-border)" }}
          >
            <Logo size={40} />
          </div>

          {/* Mobile close */}
          <div
            className="mobile-show p-4 border-b flex items-center justify-between"
            style={{ borderColor: "var(--color-border)", display: "none" }}
          >
            <Logo size={40} />
            <button
              onClick={() => setSidebarOpen(false)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-muted)", fontSize: 16 }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 space-y-1" style={{ overflowY: "auto" }}>
            {USER_NAV.map(({ to, icon, label }) => (
              <SidebarLink key={to} to={to} icon={icon} label={label} />
            ))}

            {user?.role === "admin" && (
              <>
                <div
                  className="my-3 border-t text-xs uppercase px-2 pt-3 data"
                  style={{ borderColor: "var(--color-border)", color: "var(--color-muted)", letterSpacing: "0.1em" }}
                >
                  Admin
                </div>
                {ADMIN_NAV.map(({ to, icon, label, external }) => (
                  <SidebarLink key={to} to={to} icon={icon} label={label} reloadDocument={external} />
                ))}
              </>
            )}
          </nav>

          {/* User footer */}
          <div className="p-4 border-t" style={{ borderColor: "var(--color-border)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold data flex-shrink-0"
                style={{ background: "rgba(68,136,255,0.1)", border: "1px solid rgba(68,136,255,0.3)", color: "var(--color-blue)" }}
              >
                {(user?.first_name?.[0] || user?.email?.[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="data font-semibold truncate" style={{ color: "var(--color-white)", fontSize: 13 }}>
                  {user?.full_name || user?.email}
                </div>
                <span
                  className="data"
                  style={{
                    fontSize: 10, letterSpacing: "0.15em", fontWeight: 700,
                    textTransform: "uppercase",
                    color: user?.role === "admin" ? "var(--color-red)" : "var(--color-blue)",
                    border: `1px solid ${user?.role === "admin" ? "rgba(255,51,85,0.4)" : "rgba(68,136,255,0.3)"}`,
                    borderRadius: 4, padding: "1px 6px", display: "inline-block",
                    background: user?.role === "admin" ? "rgba(255,51,85,0.08)" : "rgba(68,136,255,0.08)",
                  }}
                >
                  {user?.role}
                </span>
              </div>
            </div>
            <button onClick={handleSignOut} className="btn btn-ghost w-full justify-center">
              <FontAwesomeIcon icon={faSignOutAlt} />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main
          className="relative z-10 flex-1 overflow-auto"
          style={{ minWidth: 0 }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ to, icon, label, reloadDocument }) {
  return (
    <NavLink
      to={to}
      reloadDocument={reloadDocument}
      end={to === "/"}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 12px",
        borderRadius: 6,
        fontSize: 13,
        fontFamily: "var(--font-mono)",
        letterSpacing: "0.04em",
        textDecoration: "none",
        transition: "all 0.2s",
        color: isActive ? "var(--color-blue)" : "var(--color-muted)",
        background: isActive ? "rgba(68,136,255,0.08)" : "transparent",
        border: `1px solid ${isActive ? "rgba(68,136,255,0.2)" : "transparent"}`,
      })}
    >
      <FontAwesomeIcon icon={icon} style={{ width: 14 }} />
      {label}
    </NavLink>
  );
}
